// Space-Ship decor (Level 7): five round space-station islands connected by
// big slow spaceship platforms shuttling across void gaps. Some islands
// carry a rotating spinner bar. Background is dotted with drifting
// asteroids for atmosphere.
//
// Layout positions come from Path.buildSpaceShip.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import type { MovingPlatform, PlatformState } from "./hitParadeDecor";

const ISLAND_TOP_Y = 0.18;
const ISLAND_TOP_COLOR = 0x4f5dff;
const ISLAND_TRIM = 0xffd166;
const ISLAND_BASE = 0x2a2f6b;
const SHIP_HULL = 0xc6ccff;
const SHIP_ACCENT = 0xff5fa7;
const SHIP_THRUSTER = 0x6fe0ff;
const SPINNER_BAR = 0xff8a3d;
const SPINNER_HUB = 0xffd166;

export interface SpaceShipHandle {
  group: THREE.Group;
  /** Solid black background colour to apply to the scene. */
  skyColor: THREE.Color;
  /** Static island meshes — always-supported. */
  supportMeshes: THREE.Object3D[];
  /** Moving spaceship platforms — pushed onto Game.platforms for ride physics. */
  platforms: MovingPlatform[];
  /** Per-frame tick for non-platform animation (spinners, asteroids). */
  tick: (clockMs: number, dt: number) => void;
  /** Gate s-positions for the Spawner. */
  gatePositions: number[];
  /** Player-vs-spinner bar contact, same shape as circle-spinner's.
   *  `playerY` is the player's path-relative height — used to pick which
   *  level of spinner (upper deck vs. lower rescue path) is relevant so
   *  a player jumping on the upper deck can't be hit by a low spinner
   *  10m below them. */
  barContact: (s: number, l: number, radius: number, playerY: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    barVelS: number;
    barVelL: number;
  } | null;
}

/** Island deck thickness — chosen to match the spaceship platform exactly
 *  so the player's standing height is identical on islands and platforms.
 *  Eliminates the bounce-y feel that came from stepping between two surfaces
 *  with mismatched tops. */
const ISLAND_DECK_THICKNESS = 0.55;

function buildIsland(width: number, depth: number, hasSpinner: boolean): THREE.Group {
  const g = new THREE.Group();
  // Top deck — flat blue surface. The deck's TOP must be the only "raised"
  // mesh in the island; every other decoration hangs BELOW it so the
  // support raycaster doesn't snag a rim or trim and bounce the player.
  const top = new THREE.Mesh(
    new SoftBox(width, ISLAND_DECK_THICKNESS, depth),
    new THREE.MeshStandardMaterial({
      color: ISLAND_TOP_COLOR,
      roughness: 0.45,
      metalness: 0.18,
      emissive: 0x1a2080,
      emissiveIntensity: 0.18,
    }),
  );
  top.receiveShadow = true;
  top.castShadow = true;
  g.add(top);
  // Gold rim — placed BELOW the deck top as a hanging skirt (not a raised
  // lip). Its top stays flush with the deck top so it doesn't add a step.
  const rim = new THREE.Mesh(
    new SoftBox(width + 0.24, 0.2, depth + 0.24),
    new THREE.MeshStandardMaterial({
      color: ISLAND_TRIM,
      roughness: 0.35,
      metalness: 0.35,
    }),
  );
  rim.position.y = -0.05;
  g.add(rim);
  // Hexagonal base hanging beneath.
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(Math.min(width, depth) * 0.46, Math.min(width, depth) * 0.32, 1.2, 6),
    new THREE.MeshStandardMaterial({ color: ISLAND_BASE, roughness: 0.55 }),
  );
  base.position.y = -0.85;
  g.add(base);
  // Glow ring under the deck (transparent — excluded from supportMeshes).
  const glow = new THREE.Mesh(
    new THREE.TorusGeometry(Math.min(width, depth) * 0.45, 0.12, 12, 48),
    new THREE.MeshBasicMaterial({ color: 0x6fe0ff, transparent: true, opacity: 0.7 }),
  );
  glow.rotation.x = Math.PI / 2;
  glow.position.y = -0.45;
  g.add(glow);
  if (hasSpinner) {
    // Small accent post — sits on top of the deck under the spinner pivot.
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.32, 0.42, 0.55, 16),
      new THREE.MeshStandardMaterial({ color: SPINNER_HUB, roughness: 0.45 }),
    );
    post.position.y = ISLAND_DECK_THICKNESS / 2 + 0.27;
    g.add(post);
  }
  return g;
}

function buildSpinner(radius: number): THREE.Group {
  const g = new THREE.Group();
  const barHalfLen = radius - 0.3;
  const barThick = 0.5;
  const arm = new THREE.Mesh(
    new THREE.CapsuleGeometry(barThick, barHalfLen * 2, 12, 24),
    new THREE.MeshStandardMaterial({
      color: SPINNER_BAR,
      emissive: SPINNER_BAR,
      emissiveIntensity: 0.18,
      roughness: 0.45,
    }),
  );
  arm.rotation.z = Math.PI / 2;
  arm.castShadow = true;
  g.add(arm);
  // Bright caps.
  const capMat = new THREE.MeshStandardMaterial({ color: SPINNER_HUB, roughness: 0.4 });
  for (const x of [-barHalfLen, barHalfLen]) {
    const cap = new THREE.Mesh(new THREE.SphereGeometry(barThick * 1.3, 16, 14), capMat);
    cap.position.x = x;
    g.add(cap);
  }
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.6, 0.5, 18),
    new THREE.MeshStandardMaterial({ color: SPINNER_HUB, roughness: 0.4 }),
  );
  hub.position.y = -0.25;
  g.add(hub);
  return g;
}

function buildShipPlatform(halfWidth: number, halfDepth: number, thickness: number): THREE.Group {
  const g = new THREE.Group();
  // Oval ship hull — wide flat top so the player has plenty of room.
  const body = new THREE.Mesh(
    new SoftBox(halfWidth * 2, thickness, halfDepth * 2),
    new THREE.MeshStandardMaterial({
      color: SHIP_HULL,
      roughness: 0.4,
      metalness: 0.15,
    }),
  );
  body.castShadow = true;
  body.receiveShadow = true;
  g.add(body);
  // Accent stripe along the front edge.
  const accent = new THREE.Mesh(
    new SoftBox(halfWidth * 1.9, 0.14, 0.6),
    new THREE.MeshStandardMaterial({
      color: SHIP_ACCENT,
      emissive: SHIP_ACCENT,
      emissiveIntensity: 0.25,
      roughness: 0.45,
    }),
  );
  accent.position.set(0, thickness / 2 + 0.07, halfDepth - 0.4);
  g.add(accent);
  // Side wings.
  for (const x of [-1, 1] as const) {
    const wing = new THREE.Mesh(
      new SoftBox(0.9, 0.3, halfDepth * 1.4),
      new THREE.MeshStandardMaterial({ color: ISLAND_TRIM, roughness: 0.4 }),
    );
    wing.position.set(x * (halfWidth + 0.3), 0, 0);
    wing.rotation.z = x * 0.25;
    g.add(wing);
  }
  // Thruster glow at the back.
  for (const x of [-0.8, 0.8]) {
    const jet = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 1.5, 14, 1, true),
      new THREE.MeshBasicMaterial({ color: SHIP_THRUSTER, transparent: true, opacity: 0.7 }),
    );
    jet.rotation.x = -Math.PI / 2;
    jet.position.set(x, -0.08, -halfDepth - 0.55);
    g.add(jet);
  }
  // Belly glow.
  const belly = new THREE.Mesh(
    new THREE.CylinderGeometry(halfWidth * 0.9, halfWidth * 1.1, 0.35, 24, 1, true),
    new THREE.MeshBasicMaterial({ color: SHIP_THRUSTER, transparent: true, opacity: 0.45 }),
  );
  belly.position.y = -thickness / 2 - 0.15;
  g.add(belly);
  return g;
}

/** Dense distant starfield rendered as PointsMaterial. Lives far outside
 *  the play volume so the camera never clips into it. */
function buildStarfield(group: THREE.Group, totalLength: number) {
  const COUNT = 2400;
  const positions = new Float32Array(COUNT * 3);
  const colors = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    // Distribute on a giant cylinder around the course (player runs +Z).
    const angle = Math.random() * Math.PI * 2;
    const radius = 80 + Math.random() * 140;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius + 20;
    const z = -40 + Math.random() * (totalLength + 80);
    positions[i * 3 + 0] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    // Subtle blue/pink tint variation; mostly white.
    const t = Math.random();
    if (t < 0.7) {
      colors[i * 3 + 0] = 1; colors[i * 3 + 1] = 1; colors[i * 3 + 2] = 1;
    } else if (t < 0.85) {
      colors[i * 3 + 0] = 0.7; colors[i * 3 + 1] = 0.8; colors[i * 3 + 2] = 1;
    } else {
      colors[i * 3 + 0] = 1; colors[i * 3 + 1] = 0.75; colors[i * 3 + 2] = 0.9;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.PointsMaterial({
    size: 0.9,
    sizeAttenuation: true,
    vertexColors: true,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  });
  const stars = new THREE.Points(geo, mat);
  group.add(stars);
}

/** Distant planets — large coloured spheres parked far from the path so
 *  they feel like astronomical bodies rather than props. */
function buildPlanets(group: THREE.Group, totalLength: number) {
  const PLANETS = [
    { color: 0xff8a5b, ring: false, radius: 14, x: -85, y: 32, zT: 0.18 },
    { color: 0x5bb6ff, ring: true,  radius: 18, x: 95,  y: 38, zT: 0.55 },
    { color: 0xc9b5ff, ring: false, radius: 11, x: -70, y: 48, zT: 0.82 },
    { color: 0xffd86b, ring: false, radius: 8,  x: 70,  y: 18, zT: 0.05 },
  ];
  for (const p of PLANETS) {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(p.radius, 32, 24),
      new THREE.MeshStandardMaterial({
        color: p.color,
        roughness: 0.85,
        emissive: p.color,
        emissiveIntensity: 0.18,
      }),
    );
    sphere.position.set(p.x, p.y, p.zT * totalLength);
    group.add(sphere);
    if (p.ring) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(p.radius * 1.4, p.radius * 1.9, 64),
        new THREE.MeshBasicMaterial({
          color: 0xffd9a3,
          transparent: true,
          opacity: 0.55,
          side: THREE.DoubleSide,
          depthWrite: false,
        }),
      );
      ring.rotation.x = -Math.PI / 2.6;
      ring.position.copy(sphere.position);
      group.add(ring);
    }
  }
}

function buildAsteroidField(group: THREE.Group, totalLength: number, halfWidth: number) {
  // 80 random asteroids scattered around the course. They drift slowly each
  // frame so the space feels alive without obstructing the play space.
  const COUNT = 80;
  const geo = new THREE.IcosahedronGeometry(1, 0);
  const mat = new THREE.MeshStandardMaterial({ color: 0x6a6e8c, roughness: 0.85, flatShading: true });
  const inst = new THREE.InstancedMesh(geo, mat, COUNT);
  const tmp = new THREE.Object3D();
  const offsets: { x: number; y: number; z: number; spin: number; phase: number; scale: number }[] = [];
  for (let i = 0; i < COUNT; i++) {
    // Place outside the play tube — left, right, above, below.
    const side = Math.random() < 0.5 ? -1 : 1;
    const dist = halfWidth + 6 + Math.random() * 14;
    const x = side * dist;
    const y = -3 + Math.random() * 16;
    const z = -10 + Math.random() * (totalLength + 20);
    const scale = 0.7 + Math.random() * 2.2;
    tmp.position.set(x, y, z);
    tmp.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    tmp.scale.setScalar(scale);
    tmp.updateMatrix();
    inst.setMatrixAt(i, tmp.matrix);
    offsets.push({
      x, y, z,
      spin: (Math.random() - 0.5) * 0.3,
      phase: Math.random() * Math.PI * 2,
      scale,
    });
  }
  group.add(inst);
  return (clockMs: number) => {
    const tSec = clockMs / 1000;
    for (let i = 0; i < COUNT; i++) {
      const o = offsets[i]!;
      // Gentle bob + slow rotation around Y.
      const bob = Math.sin(tSec * 0.3 + o.phase) * 0.4;
      tmp.position.set(o.x, o.y + bob, o.z);
      tmp.rotation.set(
        o.spin * tSec,
        o.spin * tSec * 1.3,
        o.spin * tSec * 0.7,
      );
      tmp.scale.setScalar(o.scale);
      tmp.updateMatrix();
      inst.setMatrixAt(i, tmp.matrix);
    }
    inst.instanceMatrix.needsUpdate = true;
  };
}

function buildShipMovingPlatform(
  path: Path,
  gap: { start: number; end: number },
  clockOrigin: number,
  opts: { phaseOffset: number; lateral: number },
): MovingPlatform {
  // Doubled in size from the previous build — the smaller platforms made
  // hopping between two moving ships feel pixel-perfect on a tablet.
  const halfWidth = 5.2;
  const halfDepth = 4.8;
  const thickness = 0.55;
  const group = buildShipPlatform(halfWidth, halfDepth, thickness);
  // Big ships shuttle slowly along the gap. Each ship's lateral lane is
  // offset so the two ships in the same gap don't overlap, and phases are
  // offset so they're rarely both at the same end at once.
  const sStart = gap.start + halfDepth - 0.6;
  const sEnd = gap.end - halfDepth + 0.6;
  const periodMs = 20000; // slow

  let lastS = sStart;
  let velocityS = 0;
  const tick = (dt: number): PlatformState => {
    const t = (Date.now() - clockOrigin) / periodMs + opts.phaseOffset;
    const phase = (t % 1) * Math.PI * 2;
    const eased = (1 - Math.cos(phase)) / 2; // 0..1..0
    const s = sStart + (sEnd - sStart) * eased;
    velocityS = dt > 0 ? (s - lastS) / dt : 0;
    lastS = s;
    const pos = path.positionAt(s, opts.lateral);
    const topY = ISLAND_TOP_Y + thickness;
    group.position.set(pos.x, pos.y + thickness / 2, pos.z);
    group.rotation.y = path.yawAt(s);
    return {
      s,
      centerL: opts.lateral,
      topY,
      halfDepth,
      halfWidth,
      halfHeight: thickness / 2,
      velocityS,
    };
  };
  return { group, tick };
}

export function buildSpaceShipCourse(path: Path, clockOrigin: number): SpaceShipHandle {
  const ss = path.spaceShip;
  if (!ss) throw new Error("buildSpaceShipCourse called on non-space-ship path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const platforms: MovingPlatform[] = [];
  const spinnerObjs: {
    pivot: THREE.Group;
    s: number;
    halfLength: number;
    thickness: number;
    angularVel: number;
    phase0: number;
    angle: number;
    /** "upper" = on the main deck, "lower" = on the rescue path. */
    level: "upper" | "lower";
    /** Lateral world offset of the spinner pivot. Only used on the lower
     *  path where spinners are staggered laterally. */
    baseLateral?: number;
  }[] = [];

  // Islands. Lift each island so the deck top matches the spaceship
  // platform top (path-local y=0.55, world y=0.73). Walking between an
  // island and a platform is now a seamless step — no vertical mismatch
  // that the raycaster would interpret as a small ledge to climb every
  // frame (which was the bounce).
  const islandWidth = 8.6;
  const ISLAND_LIFT = 0.18 + 0.55 - ISLAND_DECK_THICKNESS / 2; // = 0.455
  for (let i = 0; i < ss.islands.length; i++) {
    const isl = ss.islands[i]!;
    const len = isl.end - isl.start;
    const hasSpinner = ss.spinners.some((sp) => sp.s >= isl.start && sp.s <= isl.end);
    const islandMesh = buildIsland(islandWidth, len, hasSpinner);
    islandMesh.position.set(0, ISLAND_LIFT, isl.start + len / 2);
    group.add(islandMesh);
    // Only the deck (first child = top mesh) is pushed to supportMeshes —
    // the rim/base/glow hang below it and shouldn't act as walking surfaces.
    const deck = islandMesh.children[0];
    if (deck) supportMeshes.push(deck);
  }

  // Static checkpoint pads beneath each floating gate so the player has a
  // safe landing zone + a respawn target. Built at the same height as the
  // start/finish islands so transitions feel seamless.
  const checkpointWidth = 6.8;
  const checkpointDepth = 5.4;
  for (const gateS of ss.gates) {
    const pad = buildIsland(checkpointWidth, checkpointDepth, false);
    pad.position.set(0, ISLAND_LIFT, gateS);
    group.add(pad);
    const deck = pad.children[0];
    if (deck) supportMeshes.push(deck);
  }

  // Spinner bars (upper deck).
  for (let i = 0; i < ss.spinners.length; i++) {
    const sp = ss.spinners[i]!;
    const pivot = buildSpinner(sp.radius);
    pivot.position.set(0, 1.05, sp.s);
    group.add(pivot);
    spinnerObjs.push({
      pivot,
      s: sp.s,
      halfLength: sp.radius - 0.3,
      thickness: 0.5,
      angularVel: sp.angularVel,
      phase0: (i * 1.27) % (Math.PI * 2),
      angle: 0,
      level: "upper",
    });
  }

  // ── Lower rescue path ────────────────────────────────────────────────
  // A continuous track running ~2m below the main course. A player who
  // misses a moving-platform jump (or steps off the upper deck) lands
  // here instead of dying. Small spinners stand in their way as they
  // walk back to a ramp leading up to the next checkpoint.
  const lowerY = -2.0;
  const lowerHalfWidth = 4.0;
  const totalCourseLength = ss.islands[ss.islands.length - 1]!.end;
  const lowerPathDepth = totalCourseLength + 12;
  const lowerDeck = new THREE.Mesh(
    new SoftBox(lowerHalfWidth * 2, ISLAND_DECK_THICKNESS, lowerPathDepth),
    new THREE.MeshStandardMaterial({ color: 0x2a2f6b, roughness: 0.6, metalness: 0.1 }),
  );
  lowerDeck.position.set(0, lowerY, lowerPathDepth / 2 - 6);
  lowerDeck.receiveShadow = true;
  lowerDeck.castShadow = true;
  group.add(lowerDeck);
  supportMeshes.push(lowerDeck);

  // Glow trim down each side so the lower track reads as a sci-fi gantry.
  for (const sign of [-1, 1] as const) {
    const trim = new THREE.Mesh(
      new SoftBox(0.4, 0.2, lowerPathDepth),
      new THREE.MeshBasicMaterial({ color: 0x6fe0ff, transparent: true, opacity: 0.65 }),
    );
    trim.position.set(sign * (lowerHalfWidth + 0.15), lowerY + 0.45, lowerPathDepth / 2 - 6);
    group.add(trim);
  }

  // Ramps connecting the lower path back up to each upper-deck checkpoint
  // so a fallen player can climb out. Offset to one lateral side so they
  // don't sit under the moving platforms.
  const RAMP_LENGTH = 8.0;
  const RAMP_WIDTH = 3.0;
  for (const gateS of ss.gates) {
    const upperDeckY = ISLAND_LIFT + ISLAND_DECK_THICKNESS / 2; // 0.73
    const ramp = new THREE.Mesh(
      // 0.5m minimum thickness for any walkable surface.
      new SoftBox(RAMP_WIDTH, 0.55, RAMP_LENGTH),
      new THREE.MeshStandardMaterial({ color: 0x4f5dff, roughness: 0.55 }),
    );
    const rampMidY = (upperDeckY + lowerY) / 2;
    // Slope so the ramp's TOP end meets the checkpoint deck (at gateS)
    // and the BOTTOM end sits on the lower track. The previous +angle
    // tilted the wrong way — the high end was AWAY from the gate,
    // forcing the player to walk down the ramp to reach the checkpoint.
    const angle = Math.atan2(upperDeckY - lowerY, RAMP_LENGTH);
    ramp.rotation.x = -angle;
    ramp.position.set(RAMP_WIDTH / 2 + 1.4, rampMidY, gateS - RAMP_LENGTH / 2 - 0.2);
    ramp.receiveShadow = true;
    ramp.castShadow = true;
    group.add(ramp);
    supportMeshes.push(ramp);
  }

  // Lots of little spinners along the lower path — the kid has to weave
  // between them as they walk back up to a ramp. Spaced every ~6m.
  const lowerSpinnerRadius = 2.0;
  for (let s = 10; s < totalCourseLength - 6; s += 6) {
    // Stagger lateral position so they don't form a clean line.
    const lat = (((s / 6) | 0) % 3 - 1) * 1.4;
    const pivot = buildSpinner(lowerSpinnerRadius);
    pivot.position.set(lat, lowerY + 0.85, s);
    pivot.scale.set(0.7, 0.7, 0.7); // smaller than upper spinners
    group.add(pivot);
    spinnerObjs.push({
      pivot,
      s,
      // Halflength stored in world units; barContact below uses it
      // directly, so scale the value by the same 0.7 factor.
      halfLength: (lowerSpinnerRadius - 0.3) * 0.7,
      thickness: 0.45 * 0.7,
      angularVel: 1.4 + ((s * 0.13) % 1.0),
      phase0: (s * 0.41) % (Math.PI * 2),
      angle: 0,
      level: "lower",
      baseLateral: lat,
    });
  }

  // Moving spaceship platforms — TWO per gap, on different lateral lanes
  // and offset phases so one's always near the player while the other's
  // halfway across.
  for (const gap of ss.gaps) {
    const left = buildShipMovingPlatform(path, gap, clockOrigin, {
      phaseOffset: 0,
      lateral: 2.4,
    });
    platforms.push(left);
    group.add(left.group);
    const right = buildShipMovingPlatform(path, gap, clockOrigin, {
      phaseOffset: 0.5,
      lateral: -2.4,
    });
    platforms.push(right);
    group.add(right.group);
  }

  // Space backdrop: distant starfield + a handful of large planets +
  // foreground drifting asteroids.
  const totalLength = (ss.islands[ss.islands.length - 1]!.end);
  buildStarfield(group, totalLength);
  buildPlanets(group, totalLength);
  const asteroidTick = buildAsteroidField(group, totalLength, ss.halfWidth);

  const tick = (clockMs: number, _dt: number) => {
    const tSec = clockMs / 1000;
    for (const sp of spinnerObjs) {
      sp.angle = sp.phase0 + tSec * sp.angularVel;
      sp.pivot.rotation.y = sp.angle;
    }
    asteroidTick(clockMs);
  };

  // Spinner contact — same maths as circle-spinner's barContact.
  const barContact = (s: number, l: number, radius: number, playerY: number) => {
    // Pick which spinner set is relevant based on the player's height
    // above the path. Anything below 0 is on the rescue path; anything
    // around the deck level (≥ ~0.3) is on the upper deck.
    const onLower = playerY < -0.2;
    for (const sp of spinnerObjs) {
      if (onLower && sp.level !== "lower") continue;
      if (!onLower && sp.level !== "upper") continue;
      const dl = l - (sp.baseLateral ?? 0);
      const ds = s - sp.s;
      const cos = Math.cos(sp.angle);
      const sin = Math.sin(sp.angle);
      const localX = dl * cos - ds * sin;
      const localY = dl * sin + ds * cos;
      const halfLen = sp.halfLength;
      const halfThick = sp.thickness;
      const clampedX = Math.max(-halfLen, Math.min(halfLen, localX));
      const dxLocal = localX - clampedX;
      const dyLocal = localY;
      const dist2 = dxLocal * dxLocal + dyLocal * dyLocal;
      const sumR = halfThick + radius;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      const nxLocal = dist > 0.001 ? dxLocal / dist : 0;
      const nyLocal = dist > 0.001 ? dyLocal / dist : 1;
      const normalL = nxLocal * cos + nyLocal * sin;
      const normalS = -nxLocal * sin + nyLocal * cos;
      const barVelL = sp.angularVel * ds;
      const barVelS = -sp.angularVel * dl;
      return { normalS, normalL, penetration: sumR - dist, barVelS, barVelL };
    }
    return null;
  };

  return {
    group,
    skyColor: new THREE.Color(0x02030c),
    supportMeshes,
    platforms,
    tick,
    gatePositions: ss.gates,
    barContact,
  };
}
